
import BudgetCalculatorInputSection from '@/components/calculators/BudgetCalculatorInputSection';
import BudgetCalculatorPortfolioMixSection from '@/components/calculators/BudgetCalculatorPortfolioMixSection';
import BudgetCalculatorCalculatedMetricsSection from '@/components/calculators/BudgetCalculatorCalculatedMetricsSection';
import BudgetCalculatorCoraaSuggestionsSection from '@/components/calculators/BudgetCalculatorCoraaSuggestionsSection';
import BudgetCalculatorExportSection from '@/components/calculators/BudgetCalculatorExportSection';

import React, { useState } from 'react';

// Types for calculator inputs and portfolio mix
export interface CalculatorInputs {
  totalEmployees: number;
  participationPercent: number;
  avgVolunteerTime: number;
  volunteeringDays: number;
  volunteersPerEvent: number;
  participationType: string;
  daysOfService: boolean;
  usePaidTimeOff: boolean;
}

export interface PortfolioMixItem {
  label: string;
  share: number;
  costPerVolunteer: number;
  hoursPerActivity: number;
  beneficiariesPerActivity: number;
}

const DEFAULT_INPUTS: CalculatorInputs = {
  totalEmployees: 1000,
  participationPercent: 20,
  avgVolunteerTime: 8,
  volunteeringDays: 2,
  volunteersPerEvent: 10,
  participationType: 'Open',
  daysOfService: false,
  usePaidTimeOff: false,
};

const DEFAULT_MIX: PortfolioMixItem[] = [
  { label: 'Create & Assemble', share: 20, costPerVolunteer: 500, hoursPerActivity: 4, beneficiariesPerActivity: 20 },
  { label: 'Learn', share: 15, costPerVolunteer: 300, hoursPerActivity: 2, beneficiariesPerActivity: 10 },
  { label: 'Interact', share: 15, costPerVolunteer: 400, hoursPerActivity: 3, beneficiariesPerActivity: 12 },
  { label: 'Participate', share: 20, costPerVolunteer: 350, hoursPerActivity: 2, beneficiariesPerActivity: 15 },
  { label: 'Skills-based/Pro bono', share: 20, costPerVolunteer: 800, hoursPerActivity: 6, beneficiariesPerActivity: 30 },
  { label: 'Acts of Kindness', share: 10, costPerVolunteer: 200, hoursPerActivity: 1, beneficiariesPerActivity: 8 },
];

const CURRENCIES = [
  { code: 'INR', symbol: '₹', label: 'INR (₹)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
];

const ResourcesBudgetCalculator: React.FC = () => {
  // Centralized state
  const [inputs, setInputs] = useState<CalculatorInputs>(DEFAULT_INPUTS);
  const [portfolioMix, setPortfolioMix] = useState<PortfolioMixItem[]>(DEFAULT_MIX);
  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR');

  // --- Calculation Logic ---
  // 1. Total Volunteers
  const totalVolunteers = Math.round(inputs.totalEmployees * (inputs.participationPercent / 100));

  // 2. Total Volunteer Hours
  // Each activity: share% * totalVolunteers * hoursPerActivity
  const totalVolunteerHours = Math.round(
    portfolioMix.reduce(
      (sum, item) => sum + (item.share / 100) * totalVolunteers * item.hoursPerActivity,
      0
    )
  );

  // 3. Engaged Workforce %
  const engagedWorkforcePercent = inputs.participationPercent;

  // 4. Total Events Required
  // Events = totalVolunteers / volunteersPerEvent (rounded up)
  const totalEventsRequired = Math.ceil(totalVolunteers / (inputs.volunteersPerEvent || 1));

  // 5. Total Program Cost
  // Each activity: share% * totalVolunteers * costPerVolunteer
  const totalProgramCost = Math.round(
    portfolioMix.reduce(
      (sum, item) => sum + (item.share / 100) * totalVolunteers * item.costPerVolunteer,
      0
    )
  );

  // 6. Avg. Cost per Volunteer
  const avgCostPerVolunteer = totalVolunteers > 0 ? Math.round(totalProgramCost / totalVolunteers) : 0;

  // 7. Cost vs Impact Ratio
  // Ratio = 1 : (beneficiariesReached per 1 unit of cost)
  // beneficiariesReached per 1 unit cost = totalBeneficiaries / totalProgramCost
  // Format: 1:XX

  // 8. Beneficiaries Reached
  // Each activity: share% * totalVolunteers * beneficiariesPerActivity
  const beneficiariesReached = Math.round(
    portfolioMix.reduce(
      (sum, item) => sum + (item.share / 100) * totalVolunteers * item.beneficiariesPerActivity,
      0
    )
  );

  const costImpactRatio = totalProgramCost > 0
    ? `1:${Math.round(beneficiariesReached / totalProgramCost)}`
    : '1:0';

  // 9. Est. Attrition Reduction % (simple model: 0.25% per 10% engaged workforce, capped at 5%)
  const attritionReductionPercent = Math.min(5, Math.round((engagedWorkforcePercent / 10) * 0.25 * 100) / 100);

  // 10. SDG Alignment Tags (placeholder)
  const sdgTags = '4, 10, 17';

  // 11. % Events w/ UN Days (placeholder: 30%)
  const percentEventsWithUnDays = 30;

  const metrics = {
    totalVolunteers,
    totalVolunteerHours,
    engagedWorkforcePercent,
    totalEventsRequired,
    totalProgramCost,
    avgCostPerVolunteer,
    costImpactRatio,
    beneficiariesReached,
    attritionReductionPercent,
    sdgTags,
    percentEventsWithUnDays,
  };

  // Handlers to pass to children
  const handleInputChange = (field: keyof CalculatorInputs, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };
  const handlePortfolioMixChange = (idx: number, field: keyof PortfolioMixItem, value: any) => {
    setPortfolioMix(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };
  const handlePortfolioMixShares = (newShares: number[]) => {
    setPortfolioMix(prev => prev.map((item, i) => ({ ...item, share: newShares[i] })));
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4 md:px-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <span role="img" aria-label="calculator">🧮</span>
          Budget & Impact Calculator
        </h1>
        <div className="flex items-center gap-2">
          <label htmlFor="currency" className="text-sm font-medium text-gray-700">Currency:</label>
          <select
            id="currency"
            value={currency}
            onChange={e => setCurrency(e.target.value as 'INR' | 'USD')}
            className="border border-gray-300 rounded px-2 py-1 focus:outline-green-600"
          >
            {CURRENCIES.map(cur => (
              <option key={cur.code} value={cur.code}>{cur.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 flex flex-col gap-6">
          <BudgetCalculatorInputSection
            inputs={inputs}
            onInputChange={handleInputChange}
            currency={currency}
          />
          <BudgetCalculatorPortfolioMixSection
            portfolioMix={portfolioMix}
            onMixChange={handlePortfolioMixChange}
            onMixSharesChange={handlePortfolioMixShares}
            currency={currency}
          />
        </div>
        <div className="flex-1">
          <BudgetCalculatorCalculatedMetricsSection metrics={metrics} currency={currency} />
        </div>
      </div>
      <BudgetCalculatorCoraaSuggestionsSection metrics={metrics} />
      <BudgetCalculatorExportSection metrics={metrics} currency={currency} inputs={inputs} portfolioMix={portfolioMix} />
    </div>
  );
};

export default ResourcesBudgetCalculator;
